import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// The canonical option lists, form value type, and empty template live in
// shared/sales-discount-template (pure data — the server-side Build-with-AI
// persist path normalizes against the same source of truth). Re-exported
// here so existing imports from this module keep working unchanged.
import {
  SALE_TYPES,
  URGENCY_MECHANISMS,
  APPLIES_TO_SCOPES,
  SALE_STATUSES,
  type SalesDiscountFormValue,
} from "@shared/sales-discount-template";

export {
  SALE_TYPES,
  URGENCY_MECHANISMS,
  APPLIES_TO_SCOPES,
  SALE_STATUSES,
  type SalesDiscountFormValue,
};
export { emptySalesDiscount } from "@shared/sales-discount-template";

type Option = { value: string; label: string };

function selectField(label: string, value: string, onChange: (v: string) => void, options: Option[], testId: string) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function SalesDiscountForm({
  value, onChange,
}: { value: SalesDiscountFormValue; onChange: (v: SalesDiscountFormValue) => void }) {
  const update = (patch: Partial<SalesDiscountFormValue>) => onChange({ ...value, ...patch });
  const scope = value.appliesTo?.scope ?? "brand";
  const setScope = (s: string) => update({ appliesTo: { scope: s, productIds: value.appliesTo?.productIds ?? [], offerIds: value.appliesTo?.offerIds ?? [] } });

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Sale name *</Label>
        <Input
          value={value.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="e.g. Memorial Day Starter Kit Sale"
          data-testid="input-sale-name"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {selectField("Sale type *", value.saleType, (v) => update({ saleType: v }), SALE_TYPES, "select-sale-type")}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Discount value</Label>
          <Input
            value={value.discountValue ?? ""}
            onChange={(e) => update({ discountValue: e.target.value })}
            placeholder="e.g. 25% off · $10 off · Free shipping · Buy 2 get 1 free"
            data-testid="input-discount-value"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {selectField("Applies to", scope, setScope, APPLIES_TO_SCOPES, "select-applies-to")}
        {selectField("Status", value.status, (v) => update({ status: v }), SALE_STATUSES, "select-status")}
        {selectField("Urgency mechanism", value.urgencyMechanism ?? "no_urgency", (v) => update({ urgencyMechanism: v }), URGENCY_MECHANISMS, "select-urgency")}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Promo code</Label>
          <Input
            value={value.promoCode ?? ""}
            onChange={(e) => update({ promoCode: e.target.value })}
            placeholder="e.g. STARTER25"
            data-testid="input-promo-code"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Expiration date</Label>
          <Input
            type="date"
            value={value.expirationDate ? value.expirationDate.slice(0, 10) : ""}
            onChange={(e) => update({ expirationDate: e.target.value || null })}
            data-testid="input-expiration-date"
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Promotion summary</Label>
        <Textarea
          value={value.promotionSummary ?? ""}
          onChange={(e) => update({ promotionSummary: e.target.value })}
          placeholder="Internal summary of what this sale is and how it should be used."
          rows={2}
          data-testid="input-promotion-summary"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Customer-facing offer copy</Label>
        <Textarea
          value={value.customerFacingCopy ?? ""}
          onChange={(e) => update({ customerFacingCopy: e.target.value })}
          placeholder='e.g. "Get the full Farm to Skin Essentials Kit for $29 today only."'
          rows={2}
          data-testid="input-customer-copy"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Why this sale exists</Label>
        <Textarea
          value={value.whyThisSaleExists ?? ""}
          onChange={(e) => update({ whyThisSaleExists: e.target.value })}
          placeholder='e.g. "Designed to reduce first-purchase friction and introduce customers to the full routine."'
          rows={2}
          data-testid="input-why-exists"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">CTA language</Label>
        <Textarea
          value={value.ctaLanguage ?? ""}
          onChange={(e) => update({ ctaLanguage: e.target.value })}
          placeholder="One per line: Claim the Deal · Get the Starter Kit · Try It for $29"
          rows={2}
          data-testid="input-cta-language"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Creative notes</Label>
        <Textarea
          value={value.creativeNotes ?? ""}
          onChange={(e) => update({ creativeNotes: e.target.value })}
          placeholder='e.g. "Do not make it feel cheap. Frame it as a farmstand special, not a clearance sale."'
          rows={2}
          data-testid="input-creative-notes"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Terms &amp; conditions</Label>
        <Textarea
          value={value.termsConditions ?? ""}
          onChange={(e) => update({ termsConditions: e.target.value })}
          placeholder="Optional fine print."
          rows={2}
          data-testid="input-terms"
        />
      </div>
    </div>
  );
}
